import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Ticket } from "lucide-react";
import dayjs from "dayjs";

const satBadge = (s) => {
  if (s === "positive") return { cls: "bg-emerald-100 text-emerald-800", label: "Puas" };
  if (s === "neutral") return { cls: "bg-amber-100 text-amber-800", label: "Biasa" };
  if (s === "negative") return { cls: "bg-red-100 text-red-800", label: "Kurang Puas" };
  return { cls: "bg-zinc-100 text-zinc-600", label: "—" };
};

const vBadge = (s) => s === "Active"
  ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Aktif</Badge>
  : s === "Redeemed"
    ? <Badge className="bg-blue-100 text-blue-800 border-0">Ditukar</Badge>
    : <Badge className="bg-zinc-200 text-zinc-600 border-0">Kedaluwarsa</Badge>;

export default function Customers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vFor, setVFor] = useState(null);
  const [vData, setVData] = useState(null);
  const [vLoading, setVLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/customers");
      setItems(data); setLoading(false);
    })();
  }, []);

  const showVouchers = async (c) => {
    setVFor(c); setVLoading(true); setVData(null);
    try {
      const { data } = await api.get(`/customers/${c.id}/vouchers`);
      setVData(data);
    } catch { setVData({ summary: { total: 0, active: 0, redeemed: 0, expired: 0 }, vouchers: [] }); }
    finally { setVLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Database Pelanggan</h1>
        <p className="text-sm text-zinc-500 mt-1">Daftar pelanggan yang mengisi kontak WhatsApp.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Nama</th>
                <th className="text-left px-5 py-3 font-semibold">WhatsApp</th>
                <th className="text-left px-5 py-3 font-semibold">Outlet</th>
                <th className="text-left px-5 py-3 font-semibold">Feedback Terakhir</th>
                <th className="text-left px-5 py-3 font-semibold">Tanggal</th>
                <th className="text-left px-5 py-3 font-semibold">Voucher</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-400">Memuat...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-400">Belum ada pelanggan.</td></tr>}
              {items.map(c => {
                const b = satBadge(c.last_satisfaction);
                return (
                  <tr key={c.id} className="border-t border-zinc-100" data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-3 font-medium">{c.name || <span className="text-zinc-400">—</span>}</td>
                    <td className="px-5 py-3 text-zinc-700">{c.whatsapp}</td>
                    <td className="px-5 py-3 text-zinc-600">{c.outlet_name || "—"}</td>
                    <td className="px-5 py-3"><Badge className={`${b.cls} border-0`}>{b.label}</Badge></td>
                    <td className="px-5 py-3 text-zinc-500 whitespace-nowrap">{c.last_feedback_at ? dayjs(c.last_feedback_at).format("DD MMM YYYY") : "—"}</td>
                    <td className="px-5 py-3">
                      <Button variant="outline" size="sm" onClick={() => showVouchers(c)} data-testid={`customer-vouchers-${c.id}`}>
                        <Ticket size={13} className="mr-1" /> Riwayat
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!vFor} onOpenChange={(v) => { if (!v) { setVFor(null); setVData(null); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Riwayat Voucher — {vFor?.name || "Pelanggan"}</DialogTitle></DialogHeader>
          {vLoading && <div className="text-sm text-zinc-500 py-6 text-center">Memuat...</div>}
          {!vLoading && vData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-center" data-testid="customer-voucher-summary">
                {[
                  { label: "Total", val: vData.summary.total },
                  { label: "Aktif", val: vData.summary.active },
                  { label: "Ditukar", val: vData.summary.redeemed },
                  { label: "Kedaluwarsa", val: vData.summary.expired },
                ].map(s => (
                  <div key={s.label} className="bg-zinc-50 rounded-xl p-3">
                    <div className="text-lg font-extrabold">{s.val}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{s.label}</div>
                  </div>
                ))}
              </div>
              {vData.vouchers.length === 0
                ? <div className="text-sm text-zinc-400 text-center py-4">Belum ada voucher.</div>
                : (
                  <div className="space-y-2">
                    {vData.vouchers.map(v => (
                      <div key={v.id} className="flex items-center justify-between border border-zinc-200 rounded-xl px-4 py-2.5" data-testid={`customer-voucher-row-${v.id}`}>
                        <div>
                          <div className="text-sm font-medium">{v.coupon_name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{v.unique_code}</div>
                        </div>
                        <div className="text-right">
                          {vBadge(v.status)}
                          <div className="text-xs text-zinc-400 mt-1">{dayjs(v.issued_at).format("DD MMM YYYY")}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
